import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Target, FileText, Link2, LayoutGrid, List } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STAGES = [
  { key: "prospect", label: "Prospect", cls: "bg-slate-100 text-slate-600" },
  { key: "qualified", label: "Qualified", cls: "bg-sky-100 text-sky-700" },
  { key: "proposal", label: "Proposal", cls: "bg-indigo-100 text-indigo-700" },
  { key: "negotiation", label: "Negotiation", cls: "bg-amber-100 text-amber-700" },
  { key: "won", label: "Won", cls: "bg-green-100 text-green-700" },
  { key: "lost", label: "Lost", cls: "bg-red-100 text-red-700" },
];
const stageOf = (k) => STAGES.find((s) => s.key === k) || STAGES[0];
const EMPTY = { name: "", client_id: "none", stage: "prospect", value: 0, currency: "CAD", expected_close: "", notes: "", quote_ids: [] };

export const Opportunities = ({ clients = [], quotes = [], canCreate, canEdit, canManage, money }) => {
  const [opps, setOpps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);
  const [oppView, setOppView] = useState("board");
  const [dragId, setDragId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const load = () => api.get("/opportunities").then((r) => setOpps(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ name: o.name, client_id: o.client_id || "none", stage: o.stage, value: o.value || 0, currency: o.currency || "CAD",
      expected_close: (o.expected_close || "").slice(0, 10), notes: o.notes || "", quote_ids: o.quote_ids || [] });
    setOpen(true);
  };
  const toggleQuote = (qid) => setForm((f) => ({ ...f, quote_ids: f.quote_ids.includes(qid) ? f.quote_ids.filter((x) => x !== qid) : [...f.quote_ids, qid] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Opportunity name required"); return; }
    const payload = { ...form, client_id: form.client_id === "none" ? null : form.client_id, value: Number(form.value) || 0,
      expected_close: form.expected_close || null };
    try {
      if (editing) await api.put(`/opportunities/${editing.opportunity_id}`, payload);
      else await api.post("/opportunities", payload);
      toast.success(editing ? "Opportunity updated" : "Opportunity created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/opportunities/${delTarget.opportunity_id}`); toast.success("Opportunity deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete"); }
  };
  const changeStage = async (o, stage) => {
    if (!o || o.stage === stage) return;
    setOpps((arr) => arr.map((x) => (x.opportunity_id === o.opportunity_id ? { ...x, stage } : x)));
    try {
      await api.put(`/opportunities/${o.opportunity_id}`, { name: o.name, client_id: o.client_id || null, stage,
        value: o.value || 0, currency: o.currency || "CAD", expected_close: o.expected_close || null, notes: o.notes || "", quote_ids: o.quote_ids || [] });
      load();
    } catch (e) { toast.error("Failed to move opportunity"); load(); }
  };
  const quoteOptions = quotes.filter((q) => form.client_id === "none" || !form.client_id || q.client_id === form.client_id);

  const openOpps = opps.filter((o) => o.stage !== "won" && o.stage !== "lost");
  const openValue = openOpps.reduce((s, o) => s + (o.value || 0), 0);
  const wonValue = opps.filter((o) => o.stage === "won").reduce((s, o) => s + (o.value || 0), 0);

  return (
    <div className="space-y-4" data-testid="opportunities-panel">
      <div className="flex items-center justify-between">
        <div className="flex gap-3 text-sm">
          <span data-testid="opp-open-count"><span className="font-semibold text-slate-800">{openOpps.length}</span> <span className="text-slate-400">open</span></span>
          <span data-testid="opp-open-value"><span className="font-semibold text-teal-600">{money(openValue)}</span> <span className="text-slate-400">in pipeline</span></span>
          <span data-testid="opp-won-value"><span className="font-semibold text-green-600">{money(wonValue)}</span> <span className="text-slate-400">won</span></span>
        </div>
        {canCreate && <Button data-testid="new-opportunity-button" onClick={openNew} className="bg-teal-500 hover:bg-teal-600 text-white"><Plus className="h-4 w-4" /> New Opportunity</Button>}
      </div>

      <div className="flex justify-end">
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
          <button data-testid="opp-view-board" onClick={() => setOppView("board")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium ${oppView === "board" ? "bg-teal-500 text-white" : "text-slate-500 hover:text-slate-700"}`}><LayoutGrid className="h-4 w-4" /> Board</button>
          <button data-testid="opp-view-table" onClick={() => setOppView("table")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium ${oppView === "table" ? "bg-teal-500 text-white" : "text-slate-500 hover:text-slate-700"}`}><List className="h-4 w-4" /> Table</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        {STAGES.map((s) => {
          const list = opps.filter((o) => o.stage === s.key);
          const val = list.reduce((a, o) => a + (o.value || 0), 0);
          return (
            <div key={s.key} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`stage-card-${s.key}`}>
              <div className="text-xs uppercase tracking-wide text-slate-400">{s.label}</div>
              <div className="mt-1 text-lg font-bold text-slate-800">{list.length}</div>
              <div className="text-xs text-slate-500">{money(val)}</div>
            </div>
          );
        })}
      </div>

      {oppView === "board" && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="opp-board">
          {STAGES.map((s) => {
            const list = opps.filter((o) => o.stage === s.key);
            return (
              <div key={s.key} data-testid={`board-col-${s.key}`}
                onDragOver={(e) => { e.preventDefault(); if (dragOverStage !== s.key) setDragOverStage(s.key); }}
                onDrop={() => { changeStage(opps.find((x) => x.opportunity_id === dragId), s.key); setDragId(null); setDragOverStage(null); }}
                className={`rounded-lg border p-2 transition-colors ${dragOverStage === s.key ? "border-teal-400 bg-teal-50" : "border-slate-200 bg-slate-50/60"}`}>
                <div className="mb-2 flex items-center justify-between px-1">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>
                  <span className="text-xs text-slate-400">{list.length}</span>
                </div>
                <div className="min-h-[48px] space-y-2">
                  {list.map((o) => (
                    <div key={o.opportunity_id} draggable={canEdit} onDragStart={() => setDragId(o.opportunity_id)} onDragEnd={() => { setDragId(null); setDragOverStage(null); }}
                      data-testid={`board-card-${o.opportunity_id}`}
                      className={`rounded-md border border-slate-200 bg-white p-2 shadow-sm ${canEdit ? "cursor-move" : ""} ${dragId === o.opportunity_id ? "opacity-40" : ""}`}>
                      <div className="flex items-start justify-between gap-1">
                        <span className="text-sm font-medium text-slate-800">{o.name}</span>
                        {canEdit && <button onClick={() => openEdit(o)} className="shrink-0 text-slate-300 hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>}
                      </div>
                      {(o.client?.name || o.client?.company) && <div className="text-xs text-slate-400">{o.client?.name || o.client?.company}</div>}
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-xs font-semibold text-teal-600">{money(o.value, o.currency)}</span>
                        {o.quotes?.length ? <span className="inline-flex items-center gap-0.5 text-xs text-slate-400"><Link2 className="h-3 w-3" />{o.quotes.length}</span> : null}
                      </div>
                    </div>
                  ))}
                  {list.length === 0 && <div className="rounded-md border border-dashed border-slate-200 py-3 text-center text-xs text-slate-300">Drop here</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {oppView === "table" && (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
            <th className="p-3">Opportunity</th><th className="p-3">Client</th><th className="p-3">Stage</th>
            <th className="p-3 text-right">Value</th><th className="p-3">Close date</th><th className="p-3">Quotes</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {opps.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400">No opportunities yet.</td></tr>
            ) : opps.map((o) => (
              <tr key={o.opportunity_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`opp-row-${o.opportunity_id}`}>
                <td className="p-3 font-medium text-slate-800">{o.name}</td>
                <td className="p-3 text-slate-600">{o.client?.name || o.client?.company || "—"}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${stageOf(o.stage).cls}`}>{stageOf(o.stage).label}</span></td>
                <td className="p-3 text-right text-slate-700">{money(o.value, o.currency)}</td>
                <td className="p-3 text-slate-500">{o.expected_close ? o.expected_close.slice(0, 10) : "—"}</td>
                <td className="p-3 text-slate-500">{o.quotes && o.quotes.length ? <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" />{o.quotes.length}</span> : "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canEdit && <button data-testid={`edit-opp-${o.opportunity_id}`} onClick={() => openEdit(o)} className="mr-2 text-slate-400 hover:text-primary"><Pencil className="h-4 w-4" /></button>}
                  {canManage && <button data-testid={`delete-opp-${o.opportunity_id}`} onClick={() => setDelTarget(o)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-teal-500" /> {editing ? "Edit Opportunity" : "New Opportunity"}</DialogTitle>
            <DialogDescription>Track a deal through your pipeline. Linking a quote is optional.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="opp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme — 50 seat UCaaS" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, quote_ids: [] })}>
                  <SelectTrigger data-testid="opp-client-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No client</SelectItem>{clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name || c.company}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Stage</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger data-testid="opp-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Value</Label><Input data-testid="opp-value-input" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CAD">CAD</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Close date</Label><Input type="date" value={form.expected_close} onChange={(e) => setForm({ ...form, expected_close: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Linked quotes <span className="text-xs font-normal text-slate-400">(optional)</span></Label>
              {quoteOptions.length === 0 ? <p className="text-xs text-slate-400">No quotes available{form.client_id !== "none" ? " for this client" : ""}.</p> : (
                <div className="max-h-32 space-y-1 overflow-y-auto rounded border border-slate-200 p-2">
                  {quoteOptions.map((q) => (
                    <label key={q.quote_id} className="flex items-center gap-2 text-sm" data-testid={`opp-quote-${q.quote_id}`}>
                      <input type="checkbox" checked={form.quote_ids.includes(q.quote_id)} onChange={() => toggleQuote(q.quote_id)} />
                      <FileText className="h-3.5 w-3.5 text-slate-400" /> #{q.number} {q.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-opp-button" onClick={save} className="bg-teal-500 hover:bg-teal-600 text-white">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete opportunity?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove "{delTarget?.name}".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-opp" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
